<script context="module" lang="ts">
	import Section from '$lib/components/layout/Section.svelte';
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import { gql } from 'graphql-request';

	export const config: SectionConfig = {
		collection: 'section_press',
		availableFor: ['pages'],
		query: gql`
        internal_name
        translations {
            languages_code {
                code
            }
            title
            text
            contact_title
            download_title
        }
        press_kits {
            section_press_item_id {
                thumbnail {
                    ...Media
                }
                file {
                    ...Media
                }
                translations {
                    languages_code {
                        code
                    }
                    title
                }
            }
        }
		people {
			section_press_person_id {
				media {
					...Media
				}
				name
				position
				phone
				email
			}
		}

    `
	};

	// optional: define a type for the props (note: injectTranslations is applied)
	type Props = {
		internal_name: string;
		title: string;
		text: string;
		contact_title: string;
		download_title: string;
		press_kits: any;
		people: any;
	};
</script>

<script lang="ts">
	import { PUBLIC_DIRECTUS_BACKEND } from '$env/static/public';
	import Wysiwyg from '$lib/components/inputs/WYSIWYG.svelte';
	import GlobalMedia from '$lib/components/media/GlobalMedia.svelte';
	import Media from '$lib/components/media/Media.svelte';
	import DirectusLink from '$lib/components/navigation/DirectusLink.svelte';
	import JSZip from 'jszip';

	import * as fileSaver from 'file-saver';
	const { saveAs } = fileSaver;

	// import { saveAs } from 'file-saver';
	import Button from '$lib/components/util/Button.svelte';

	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)

	async function downloadAllPressKits() {
		const zip = new JSZip();
		const promises = data.press_kits.map(async ({ section_press_item_id: item }) => {
			const fileUrl = `${PUBLIC_DIRECTUS_BACKEND}assets/${item.file.id}`;
			const response = await fetch(fileUrl);
			const blob = await response.blob();
			zip.file(`${item.title}.pdf`, blob);
		});

		await Promise.all(promises);
		zip.generateAsync({ type: 'blob' }).then(function (content) {
			saveAs(content, 'press_kits.zip');
		});
	}
</script>

<Section class="press-section" gapType="padding">
	<div class="title">{data.title}</div>
	<div class="text"><Wysiwyg text={data.text} /></div>
	<div class="contact-title">{data.contact_title}</div>
	<div class="contact-wrapper">
		{#each data.people as { section_press_person_id: item }}
			<div class="contact">
				<div class="image-wrapper">
					<Media {...item.media} fit="cover" transform="l" />
				</div>
				<div class="info-wrapper">
					<div>
						{#if item.name}
							<div class="name">{item.name}</div>
						{/if}
						{#if item.position}
							<div class="position">{item.position}</div>
						{/if}
					</div>
					<div>
						{#if item.email}
							<div>
								<a href={'mailto:' + item.email}>{item.email}</a>
							</div>
						{/if}
						{#if item.phone}
							<div>
								<a href={'tel:' + item.phone}>{item.phone}</a>
							</div>
						{/if}
					</div>
				</div>
			</div>
		{/each}
	</div>
	<div class="download-title">{data.download_title}</div>
	<div class="download-kits">
		{#each data.press_kits as { section_press_item_id: item }}
			<a target="_blank" href={PUBLIC_DIRECTUS_BACKEND + 'assets/' + item.file.id}>
				<div class="download-link">
					<div class="thumbnail"><Media {...item.thumbnail} fit="cover" transform="l" /></div>
					<div class="item-title-wrapper">
						<div class="item-title">
							{item.title}
						</div>
						<span class="arrow"><GlobalMedia key="icon-arrow-down" /></span>
					</div>
				</div>
			</a>
		{/each}
	</div>
	<Button
		variant="outline"
		on:click={downloadAllPressKits}
		textKey="Download All"
		iconKey="icon-arrow-down"
	/>
</Section>

<style lang="scss">
	:global(.press-section .inner-section) {
		max-width: v(max-width-medium) !important;
	}
	.title {
		@include font(h1);
		border-bottom: solid 1px color(black);
		padding-bottom: v(content-gap-xl);
	}
	.text {
		padding-top: v(content-gap-xl);
		@include font(text);
		max-width: 70ch;
	}
	.contact-title {
		padding: v(content-padding-lg) 0;
		@include font(h5);
	}
	.contact-wrapper {
		display: flex;
		flex-direction: column;
		gap: v(content-gap-2xl);
		@include media(phone) {
			gap: v(content-gap);
		}
	}
	.contact {
		display: flex;
		gap: v(content-gap-sm);
	}
	.image-wrapper {
		width: 100px;
		height: 125px;
	}
	.position {
		@include font(h6);
	}
	.info-wrapper {
		display: flex;
		flex-direction: column;
		justify-content: space-between;

		:global(a) {
			color: color(black);
			text-decoration: none;
			&:hover {
				text-decoration: underline;
			}
		}
	}
	.download-title {
		margin-top: v(content-gap-4xl);
		padding-bottom: v(content-padding-sm);
		@include font(h5);

		@include media(phone) {
			margin-top: v(content-gap-2xl);
		}
	}
	.download-kits {
		padding: v(content-gap) 0;
		display: flex;
		gap: v(content-gap-2xl);

		flex-wrap: wrap;
		@include media(phone) {
			gap: v(content-gap);
		}
		:global(a) {
			color: color(black);
			text-decoration: none;
			&:hover {
				text-decoration: underline;
			}
		}
	}
	.item-title-wrapper {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 150px;
		gap: v(content-gap-xs);
	}
	.item-title {
		@include font(h6);
		display: flex;
		align-items: center;
		justify-content: center;
		text-align: start;
	}
	.arrow {
		:global(svg) {
			height: 1rem;
			width: 1rem;
		}
	}
	.download-link {
		display: flex;
		width: auto;
		flex-direction: column;
	}
	.thumbnail {
		width: 150px;
		height: 150px;
		margin-bottom: v(content-gap-xs);
		:global(img) {
			border-radius: v(border-radius);
		}
	}
</style>
